import { useTranslation } from 'react-i18next'
import { Filters } from 'types/general'

import FilterCheckboxList from './FilterCheckboxList'
import FilterLocationInputs from './FilterLocationInputs'
import FilterMultiCheckbox from './FilterMultiCheckbox'
import FilterPriceRange from './FilterPriceRange'
import FilterRadioGroup from './FilterRadioGroup'
import { RESIDENTIAL_UNIT_TYPE_OPTIONS } from './residentialUnitTypes'

interface FilterComponentProps {
  filterOptions: any
  filters: Filters
  setFilters: (filters: Filters) => void
  handleChange: (key: string, value: any) => void
  onReset?: () => void
  hasAppliedFilters: boolean
}

const DEFAULT_BEDROOM_OPTIONS = [
  { value: 'studio', label: 'createAd.flow.rooms.studio' },
  { value: '2.5', label: 'createAd.flow.rooms.2_5' },
  { value: '3.5', label: 'createAd.flow.rooms.3_5' },
  { value: '4.5', label: 'createAd.flow.rooms.4_5' },
  { value: '5.5', label: 'createAd.flow.rooms.5_5' },
  { value: '6.5+', label: 'createAd.flow.rooms.6_5p' },
]

const DesktopFilterBar = ({
  filterOptions,
  filters,
  setFilters,
  handleChange,
  onReset,
  hasAppliedFilters,
}: FilterComponentProps) => {
  const { t } = useTranslation()

  if (!filterOptions) {
    return null
  }

  const location = filters.location || {
    zip_code: '',
    full_address: '',
    range: 10,
    city: '',
    neighborhoods: [],
  }
  const city = location.city ?? ''
  const neighborhoods = location.neighborhoods ?? []
  const zipCode = location.zip_code ?? ''

  const bedroomOptions = DEFAULT_BEDROOM_OPTIONS
  const unitTypeOptions = RESIDENTIAL_UNIT_TYPE_OPTIONS.map(opt => ({
    value: opt.value,
    label: opt.label,
  }))

  const rawBooleans = filterOptions.booleans?.options
  const booleanOptions = Array.isArray(rawBooleans)
    ? rawBooleans
    : rawBooleans
      ? Object.entries(rawBooleans).map(([value, label]) => ({ value, label: String(label) }))
      : []

  const selectedBedroom =
    Array.isArray(filters.bedrooms) && filters.bedrooms.length > 0 ? filters.bedrooms[0] : ''

  return (
    <aside className="flex w-full max-w-xs flex-col rounded-2xl border border-gray-200 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-5 py-4">
        <h2 className="text-base font-bold text-gray-900">{t('filters.filters')}</h2>
        {hasAppliedFilters && onReset ? (
          <button
            type="button"
            className="text-sm font-medium text-imovPrimary hover:underline"
            onClick={onReset}
          >
            {t('filters.clearAll')}
          </button>
        ) : null}
      </div>

      <div className="space-y-6 px-5 py-4 pb-5">
        <div className="space-y-3">
          <p className="text-sm font-bold text-gray-900">{t('filters.location')}</p>
          <FilterLocationInputs
            city={city}
            zipCode={zipCode}
            neighborhoods={neighborhoods}
            onChange={value =>
              handleChange('location', {
                ...location,
                city: value.city,
                zip_code: value.zip_code,
                neighborhoods: value.neighborhoods,
                neighborhood: '',
              })
            }
          />
        </div>

        <div className="space-y-3">
          <p className="text-sm font-bold text-gray-900">{t('filters.monthlyPrice')}</p>
          <FilterPriceRange
            priceMin={filters.priceMin}
            priceMax={filters.priceMax}
            onChange={(min, max) =>
              setFilters({ ...filters, priceMin: min, priceMax: max, price: [] })
            }
          />
        </div>

        <div className="space-y-3">
          <p className="text-sm font-bold text-gray-900">{t('filters.bedrooms.label')}</p>
          <FilterRadioGroup
            name="bedrooms"
            options={bedroomOptions}
            value={selectedBedroom}
            onChange={value => handleChange('bedrooms', value ? [value] : [])}
            columns={2}
          />
        </div>

        <div className="space-y-3">
          <p className="text-sm font-bold text-gray-900">{t('filters.housingType')}</p>
          <FilterMultiCheckbox
            options={unitTypeOptions}
            value={filters.unitType ?? []}
            onChange={value => handleChange('unitType', value)}
          />
        </div>

        {booleanOptions.length > 0 && (
          <div className="space-y-3">
            <p className="text-sm font-bold text-gray-900">{t('filters.optionsIncluded')}</p>
            <FilterCheckboxList
              options={booleanOptions}
              value={filters.booleans || {}}
              onChange={value => handleChange('booleans', value)}
            />
          </div>
        )}
      </div>
    </aside>
  )
}

export default DesktopFilterBar
